import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {ResizeObserver | null} */
  #wishlistLauncherObserver = null;

  /** @type {MutationObserver | null} */
  #wishlistLauncherMutationObserver = null;

  /** @type {number | null} */
  #wishlistLauncherOffset = null;

  /** @type {number | null} */
  #wishlistLauncherRaf = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    const variantPicker = this.#getVariantPicker();
    variantPicker?.addEventListener(
      'change',
      () => requestAnimationFrame(() => this.#syncStickyImageFromColorSwatch()),
      { signal }
    );

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    requestAnimationFrame(() => this.#syncStickyImageFromColorSwatch());
    this.#setupWishlistLauncherSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#wishlistLauncherObserver?.disconnect();
    this.#wishlistLauncherMutationObserver?.disconnect();
    this.#clearWishlistLauncherOffset();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    const productImage = this.#getProductImage();
    if (!cartIcon || !this.refs.addToCartButton || !productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const swatchImageUrl = this.#getColorSwatchImageUrl();

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#applyStickyImageUrl(swatchImageUrl);
    requestAnimationFrame(() => {
      this.#syncStickyImageFromColorSwatch();
      this.#scheduleWishlistLauncherSync();
    });
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    this.#syncStickyImageFromColorSwatch();

    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const formattedTitle = this.#formatStickyVariantTitle(variantPicker);
    if (!formattedTitle) return;
    variantTitleElement.textContent = formattedTitle;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#scheduleWishlistLauncherSync();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#clearWishlistLauncherOffset();
  }

  /**
   * Keep Wishlist Hero floating launcher above the sticky bar when it is visible.
   */
  #setupWishlistLauncherSync() {
    const { stickyBar } = this.refs;
    const { signal } = this.#abortController;

    for (const element of this.#getWishlistLauncherFixedElements()) {
      this.#rememberWishlistLauncherDefaults(element);
    }

    this.#wishlistLauncherObserver?.disconnect();
    this.#wishlistLauncherObserver = new ResizeObserver(() => {
      if (this.#isStuck) this.#syncWishlistLauncherOffset();
    });
    this.#wishlistLauncherObserver.observe(stickyBar);

    window.addEventListener(
      'resize',
      () => {
        if (this.#isStuck) this.#syncWishlistLauncherOffset();
      },
      { signal, passive: true }
    );

    window.addEventListener(
      'stoq:sticky-layout',
      () => {
        if (this.#isStuck) this.#scheduleWishlistLauncherSync();
      },
      { signal }
    );

    this.#wishlistLauncherMutationObserver?.disconnect();
    this.#wishlistLauncherMutationObserver = new MutationObserver(() => {
      if (!this.#isStuck) return;

      const launcher = document.getElementById('wishlisthero-floating-launcher-container');
      if (!(launcher instanceof HTMLElement)) return;

      for (const element of this.#getWishlistLauncherFixedElements()) {
        this.#rememberWishlistLauncherDefaults(element);
      }

      this.#scheduleWishlistLauncherSync();
    });

    this.#wishlistLauncherMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  #scheduleWishlistLauncherSync() {
    if (this.#wishlistLauncherRaf != null) {
      cancelAnimationFrame(this.#wishlistLauncherRaf);
    }
    this.#wishlistLauncherRaf = requestAnimationFrame(() => {
      this.#wishlistLauncherRaf = null;
      this.#syncWishlistLauncherOffset();
    });
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getWishlistLauncherFixedElements() {
    const container = document.getElementById('wishlisthero-floating-launcher-container');
    if (!(container instanceof HTMLElement)) return [];

    /** @type {HTMLElement[]} */
    const elements = [];

    for (const node of [container, ...container.querySelectorAll('*')]) {
      if (!(node instanceof HTMLElement)) continue;
      if (getComputedStyle(node).position === 'fixed') {
        elements.push(node);
      }
    }

    return elements.length ? elements : [container];
  }

  /**
   * @param {HTMLElement} element
   */
  #rememberWishlistLauncherDefaults(element) {
    if (element.dataset.stoqWishlistDefaultsSaved === 'true') return;

    const style = getComputedStyle(element);
    if (style.bottom !== 'auto') {
      element.dataset.stoqWishlistDefaultBottom = style.bottom;
    }
    if (style.top !== 'auto') {
      element.dataset.stoqWishlistDefaultTop = style.top;
    }
    element.dataset.stoqWishlistDefaultsSaved = 'true';
  }

  /**
   * @param {number} offsetPx
   */
  #applyWishlistLauncherOffset(offsetPx) {
    const barTop = this.refs.stickyBar.getBoundingClientRect().top;
    const gap = 16;

    for (const element of this.#getWishlistLauncherFixedElements()) {
      this.#rememberWishlistLauncherDefaults(element);

      const style = getComputedStyle(element);
      const usesTop = style.bottom === 'auto' && style.top !== 'auto';

      if (usesTop) {
        // Only measure height when using top positioning
        const height = element.getBoundingClientRect().height;
        element.style.setProperty('top', `${Math.max(0, barTop - gap - height)}px`, 'important');
        element.style.removeProperty('bottom');
      } else {
        element.style.setProperty('bottom', `${offsetPx}px`, 'important');
        element.style.removeProperty('top');
      }
    }

    document.body.classList.add('sticky-atc-bar-visible');
  }

  #syncWishlistLauncherOffset() {
    if (!this.#isStuck || this.refs.stickyBar?.dataset.stuck !== 'true') {
      this.#clearWishlistLauncherOffset();
      return;
    }

    const launcher = document.getElementById('wishlisthero-floating-launcher-container');
    if (!(launcher instanceof HTMLElement)) return;

    const barRect = this.refs.stickyBar.getBoundingClientRect();
    const gap = 16;
    const offset = Math.ceil(window.innerHeight - barRect.top + gap);

    this.#wishlistLauncherOffset = offset;
    this.#applyWishlistLauncherOffset(offset);
  }

  #clearWishlistLauncherOffset() {
    if (this.#wishlistLauncherRaf != null) {
      cancelAnimationFrame(this.#wishlistLauncherRaf);
      this.#wishlistLauncherRaf = null;
    }
    this.#wishlistLauncherOffset = null;

    for (const element of this.#getWishlistLauncherFixedElements()) {
      this.#restoreWishlistLauncherPosition(element);
    }

    document.body.classList.remove('sticky-atc-bar-visible');
    document.documentElement.style.removeProperty('--sticky-atc-wishlist-offset');
  }

  /**
   * @param {HTMLElement} element
   */
  #restoreWishlistLauncherPosition(element) {
    const defaultBottom = element.dataset.stoqWishlistDefaultBottom;
    const defaultTop = element.dataset.stoqWishlistDefaultTop;

    if (defaultTop) {
      element.style.setProperty('top', defaultTop, 'important');
    } else {
      element.style.removeProperty('top');
    }

    if (defaultBottom) {
      element.style.setProperty('bottom', defaultBottom, 'important');
    } else if (!defaultTop) {
      element.style.setProperty('bottom', '16px', 'important');
    } else {
      element.style.removeProperty('bottom');
    }
  }

  // Helper methods
  /**
   * Color option first, then size/spec options (matches sticky bar Liquid).
   * @param {HTMLElement} variantPicker
   * @returns {string}
   */
  #formatStickyVariantTitle(variantPicker) {
    /** @type {string[]} */
    const parts = [];

    const colorFieldset = variantPicker.querySelector('fieldset.variant-option--swatches');
    const colorInput = colorFieldset?.querySelector('input:checked');
    if (colorInput instanceof HTMLInputElement && colorInput.value.trim()) {
      parts.push(colorInput.value.trim());
    }

    for (const fieldset of variantPicker.querySelectorAll('fieldset')) {
      if (fieldset === colorFieldset) continue;
      const checked = fieldset.querySelector('input:checked');
      if (checked instanceof HTMLInputElement && checked.value.trim()) {
        parts.push(checked.value.trim());
      }
    }

    for (const select of variantPicker.querySelectorAll('select.variant-option__select')) {
      if (!select.selectedOptions.length) continue;
      const value = select.selectedOptions[0].value.trim();
      if (value) parts.push(value);
    }

    return parts.join(' / ');
  }

  /**
   * @returns {HTMLImageElement | null}
   */
  #getProductImage() {
    const image = this.refs.productImage ?? this.querySelector('.sticky-add-to-cart__image-img');
    return image instanceof HTMLImageElement ? image : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const section = this.closest('.shopify-section');
    const scopedPicker = section?.querySelector(
      `variant-picker[data-product-id="${productId}"][data-template-product-match="true"]`
    );
    if (scopedPicker) return scopedPicker;

    return document.querySelector(`variant-picker[data-product-id="${productId}"]`);
  }

  /**
   * Match the sticky bar image to the selected color swatch (same source as the picker UI).
   */
  #syncStickyImageFromColorSwatch() {
    this.#applyStickyImageUrl(this.#getColorSwatchImageUrl());
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getCheckedColorSwatchInput() {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return null;

    const checked = variantPicker.querySelector('fieldset.variant-option--swatches input:checked');
    return checked instanceof HTMLInputElement ? checked : null;
  }

  /**
   * @returns {string | null}
   */
  #getColorSwatchImageUrl() {
    const checked = this.#getCheckedColorSwatchInput();
    if (!checked) return null;

    const datasetUrl = checked.dataset.swatchImageUrl?.trim();
    if (datasetUrl) return this.#normalizeShopifyImageUrl(datasetUrl, 120);

    const swatch = checked.closest('label')?.querySelector('.swatch');
    if (!swatch) return null;

    const inlineBackground = swatch.style.getPropertyValue('--swatch-background').trim();
    let url = this.#parseCssUrl(inlineBackground);

    if (!url) {
      const computed = getComputedStyle(swatch);
      url = this.#parseCssUrl(computed.getPropertyValue('--swatch-background'));
      if (!url) url = this.#parseCssUrl(computed.backgroundImage);
    }

    return url ? this.#normalizeShopifyImageUrl(url, 120) : null;
  }

  /**
   * @param {string | null} url
   */
  #applyStickyImageUrl(url) {
    if (!url) return;

    const imageEl = this.#getProductImage();
    if (!imageEl) return;

    if (this.#imageUrlsMatch(imageEl.src, url)) return;

    imageEl.src = url;
    imageEl.removeAttribute('srcset');

    const checked = this.#getCheckedColorSwatchInput();
    const label = checked?.getAttribute('aria-label');
    if (label) imageEl.alt = label;
  }

  /**
   * @param {string} value
   * @returns {string | null}
   */
  #parseCssUrl(value) {
    if (!value || value === 'none') return null;
    const match = value.match(/url\(\s*(['"]?)([^'")]+)\1\s*\)/);
    return match?.[2] ?? null;
  }

  /**
   * @param {string} left
   * @param {string} right
   * @returns {boolean}
   */
  #imageUrlsMatch(left, right) {
    return this.#imageUrlPath(left) === this.#imageUrlPath(right);
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #imageUrlPath(url) {
    try {
      const absolute = url.startsWith('//')
        ? `https:${url}`
        : url.startsWith('/')
          ? `${window.location.origin}${url}`
          : url;
      const parsed = new URL(absolute);
      return `${parsed.pathname}${parsed.search}`;
    } catch {
      return url;
    }
  }

  /**
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #normalizeShopifyImageUrl(url, width) {
    try {
      const absolute = url.startsWith('//')
        ? `https:${url}`
        : url.startsWith('/')
          ? `${window.location.origin}${url}`
          : url;
      const parsed = new URL(absolute);
      if (parsed.pathname.includes('/cdn/shop/')) {
        parsed.searchParams.set('width', String(width));
      }
      return parsed.toString();
    } catch {
      return url.startsWith('//') ? `https:${url}` : url;
    }
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    if (!quantityDisplay || !quantityNumber) return;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
